import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../Logo";
import { useLang } from "../../lib/i18n";
import { parseLinks } from "../../lib/api";
import { goBooking } from "../../lib/promo";

const LINKS = [
  { key: "nav.about", id: "about" },
  { key: "nav.stay", id: "villas" },
  { key: "nav.dining", id: "dining" },
  { key: "nav.experiences", id: "experiences" },
  { key: "nav.offers", id: "offers" },
  { key: "nav.schedule", id: "boat-schedule" },
  { key: "nav.gallery", id: "gallery" },
  { key: "nav.blog", id: "blog" },
  { key: "nav.contact", id: "contact" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar({ bookingUrl, logoUrl, logoLightUrl, navMenu, hiddenSections = [] }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);

  // Navigate to a homepage section from any page; open full URLs directly.
  const goTo = (target) => {
    if (!target) return;
    if (/^https?:\/\//.test(target)) {
      window.open(target, "_blank");
      return;
    }
    if (target.startsWith("/") && !target.startsWith("/#")) {
      navigate(target);
      return;
    }
    const id = target.replace(/^\/#/, "").replace(/^#/, "");
    if (document.getElementById(id)) scrollToId(id);
    else navigate(`/#${id}`);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpen(false);
    };
    const onEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const customLinks = parseLinks(navMenu);
  const hiddenSet = new Set(hiddenSections);
  const isHidden = (id) => hiddenSet.has(id) || hiddenSet.has(String(id || "").replace(/^\/#/, "").replace(/^#/, ""));
  const links = (customLinks.length
    ? customLinks.map((l) => ({ id: l.url, label: l.label }))
    : LINKS.map((l) => ({ id: l.id, label: t(l.key) }))
  ).filter((l) => !isHidden(l.id));

  const onBook = () => {
    // Opens the embedded booking page (/book) so guests stay on our domain
    if (!bookingUrl) {
      toast.info(t("book.notConfigured"));
      return;
    }
    goBooking(bookingUrl);
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#FBF9F5]/95 backdrop-blur-md border-b border-[#E5DED4]/60 shadow-[0_10px_35px_rgba(30,26,23,0.08)]"
            : "bg-transparent border-b border-white/10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => {
              if (pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
              else navigate("/");
            }}
            className="shrink-0"
          >
            <Logo dark={scrolled} logoUrl={logoUrl} logoLightUrl={logoLightUrl} />
          </button>

          <div className="flex items-center gap-6">
            <nav className="hidden sm:flex items-center gap-6">
              {links
                .filter((l) => ["about", "offers"].includes(l.id))
                .map((l) => (
                  <button
                    key={l.id}
                    data-testid={`nav-link-${l.id}`}
                    onClick={() => goTo(l.id)}
                    className={`text-[12px] font-sans font-medium tracking-[0.18em] uppercase transition-colors duration-300 ${
                      scrolled ? "text-[#6E675F] hover:text-[#C9A66B]" : "text-white/85 hover:text-[#C9A66B]"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
            </nav>
            <button
              data-testid="nav-book-now-button"
              onClick={onBook}
              className="hidden sm:inline-flex items-center px-7 py-2.5 rounded-sm text-[12px] font-sans font-semibold tracking-[0.16em] uppercase transition-all duration-300 bg-[#9A7B66] text-[#FBF9F5] hover:bg-[#826552] hover:shadow-[0_8px_24px_rgba(154,123,102,0.35)]"
            >
              {t("nav.book")}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen((value) => !value)}
              className={`p-2 transition-colors ${scrolled ? "text-[#2C2825]" : "text-white"}`}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="site-more-menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.aside
            id="site-more-menu"
            ref={menuRef}
            data-testid="mobile-menu"
            initial={{ x: 32, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 32, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-[88px] right-4 sm:right-6 z-[70] w-max min-w-[240px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-110px)] overflow-y-auto rounded-sm border border-white/10 bg-[#1E1A17]/95 backdrop-blur-md shadow-2xl"
          >
            <nav className="flex flex-col items-start gap-4 px-7 pt-6 pb-5">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i, duration: 0.3 }}
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => goTo(l.id), 180);
                  }}
                  className="whitespace-nowrap font-serif text-xl text-[#F4EFEA] hover:text-[#C9A66B] transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-7 pb-6">
              <button
                data-testid="mobile-book-now-button"
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="w-full px-8 py-3 rounded-sm bg-[#9A7B66] hover:bg-[#826552] text-[#FBF9F5] text-sm font-semibold tracking-[0.16em] uppercase transition-colors"
              >
                {t("nav.book")}
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
